// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Users, Plus, Settings, Share2, UserPlus, UserCheck, UserX, Edit3, Trash2, Crown, ChevronRight, X, User, BookOpen, MessageCircle, Target, Heart, Trophy } from 'lucide-react';
import CrewCard from './crew/CrewCard';
import crewAPI, { type Crew, type CrewMember, type CrewJoinRequest } from '../services/crewAPI-new';
import prayerAPI, { type CrewAchievements, type CrewMemberAchievement } from '../services/prayerAPI';
import PrayerMedal from './prayer/PrayerMedal';

interface CrewSystemProps {
  isAuthenticated: boolean;
  isGuestMode: boolean;
  user: any;
  onOpenCrewEditModal?: (crew: Crew) => void;
}

const CrewSystem: React.FC<CrewSystemProps> = ({ isAuthenticated, isGuestMode, user, onOpenCrewEditModal }) => {
  // 서브 탭 상태
  const [activeSubTab, setActiveSubTab] = useState<'my-crews' | 'official-crews'>('my-crews');
  
  // 크루 상태 관리
  const [myCrews, setMyCrews] = useState<Crew[]>([]);
  const [availableCrews, setAvailableCrews] = useState<Crew[]>([]);
  const [showCreateCrewModal, setShowCreateCrewModal] = useState(false);
  const [showJoinCrewModal, setShowJoinCrewModal] = useState(false);
  const [showCrewDetailModal, setShowCrewDetailModal] = useState(false);
  const [showJoinRequestsModal, setShowJoinRequestsModal] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);
  const [joinRequests, setJoinRequests] = useState<CrewJoinRequest[]>([]);
  
  // 크루 생성 폼
  const [crewName, setCrewName] = useState('');
  const [crewDescription, setCrewDescription] = useState('');
  const [crewImage, setCrewImage] = useState('');
  const [crewColor, setCrewColor] = useState('#64748b');
  const [crewMaxMembers, setCrewMaxMembers] = useState(50);
  const [isCreatingCrew, setIsCreatingCrew] = useState(false);
  
  // 로딩 상태
  const [isLoading, setIsLoading] = useState(false);
  
  // 기도 달성 상태
  const [crewAchievements, setCrewAchievements] = useState<CrewAchievements | null>(null);
  const [isLoadingAchievements, setIsLoadingAchievements] = useState(false);

  // 현재 사용자가 크루장인지 확인
  const isCrewLeader = useCallback((crew: Crew) => {
    if (isGuestMode || !user?.id) {
      return false;
    }
    return user.id === crew.leaderId;
  }, [isGuestMode, user?.id]);

  // 크루 기도 달성 상태 로드
  const loadCrewAchievements = useCallback(async (crewId: string) => {
    if (isGuestMode || !user?.id) return;
    
    try {
      setIsLoadingAchievements(true);
      const achievements = await prayerAPI.getCrewAchievements(crewId);
      setCrewAchievements(achievements);
    } catch (error) {
      console.error('크루 기도 달성 상태 로드 실패:', error);
    } finally {
      setIsLoadingAchievements(false);
    }
  }, [isGuestMode, user?.id]);

  // 멤버의 기도 달성 상태 조회
  const getMemberAchievement = useCallback((memberId: string): CrewMemberAchievement | null => {
    if (!crewAchievements) return null;
    return crewAchievements.members.find(m => m.userId === memberId) || null;
  }, [crewAchievements]);

  // 내 크루 목록 로드 (중복 방지)
  const myFetchLock = React.useRef(false);
  const loadMyCrews = useCallback(async () => {
    if (!isAuthenticated || isGuestMode) {
      setMyCrews([]);
      return;
    }

    try {
      if (myFetchLock.current) return;
      myFetchLock.current = true;
      console.log('👤 내 크루 목록 로드 시작');
      const crews = await crewAPI.getMyCrews();
      setMyCrews(crews);
      console.log('✅ 내 크루 목록 로드 완료:', crews.length, '개');
    } catch (error) {
      console.error('❌ 내 크루 목록 로드 실패:', error);
      setMyCrews([]);
    } finally {
      myFetchLock.current = false;
    }
  }, [isAuthenticated, isGuestMode]);

  // 사용 가능한 크루 목록 로드
  const fetchLockRef = React.useRef(false);
  const lastFetchedAtRef = React.useRef(0);

  const loadAvailableCrews = useCallback(async () => {
    try {
      const now = Date.now();
      if (fetchLockRef.current) {
        console.log('⏳ 크루 목록 요청 잠금 중');
        return;
      }
      if (now - lastFetchedAtRef.current < 5000) {
        console.log('⏱️ 5초 내 재요청 방지');
        return;
      }
      fetchLockRef.current = true;
      lastFetchedAtRef.current = now;
      console.log('📋 사용 가능한 크루 목록 로드 시작');
      const crews = await crewAPI.getAvailableCrews();
      // 이미 가입된 크루는 숨김
      const myIds = new Set(myCrews.map(c => c.id));
      const filtered = crews.filter(c => !myIds.has(c.id));
      setAvailableCrews(filtered);
      console.log('✅ 사용 가능한 크루 목록 로드 완료:', filtered.length, '개');
    } catch (error) {
      console.error('❌ 사용 가능한 크루 목록 로드 실패:', error);
      setAvailableCrews([]);
    } finally {
      fetchLockRef.current = false;
    }
  }, [myCrews]);

  // 크루 상세 정보 로드
  const loadCrewDetail = async (crewId: string) => {
    try {
      setIsLoading(true);
      console.log('🔄 크루 상세 정보 새로고침 중:', crewId);
      
      // 크루 상세 정보 새로고침
      const crew = await crewAPI.getCrewById(crewId);
      setSelectedCrew(crew);
      setShowCrewDetailModal(true);
      
      // 기도 달성 상태 로드
      await loadCrewAchievements(crewId);
      
      // 내 크루 목록도 새로고침하여 일관성 확보
      console.log('🔄 내 크루 목록 새로고침 중...');
      await loadMyCrews();
      
      console.log('✅ 크루 상세 정보 및 목록 새로고침 완료');
    } catch (error) {
      console.error('❌ 크루 상세 정보 로드 실패:', error);
      alert('크루 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 크루 생성
  const handleCreateCrew = async () => {
    if (!crewName.trim()) {
      alert('크루명을 입력해주세요.');
      return;
    }

    try {
      setIsCreatingCrew(true);
      const newCrew = await crewAPI.createCrew({
        name: crewName.trim(),
        description: crewDescription.trim() || undefined,
        image: crewImage.trim() || undefined,
        color: crewColor,
        maxMembers: crewMaxMembers
      });

      console.log('✅ 크루 생성 완료:', newCrew);
      
      // 폼 초기화
      setCrewName('');
      setCrewDescription('');
      setCrewImage('');
      setCrewColor('#64748b');
      setCrewMaxMembers(50);
      setShowCreateCrewModal(false);
      
      // 즉시 UI에 반영 (서버 응답 활용)
      console.log('🔄 생성된 크루를 즉시 UI에 추가');
      setMyCrews(prev => [...prev, newCrew]);
      
      // 캐시 타임스탬프 초기화하여 다음 로드 허용
      lastFetchedAtRef.current = 0;
      
      // 백그라운드에서 전체 목록 새로고침 (데이터 동기화)
      loadMyCrews().catch(err => console.error('백그라운드 새로고침 실패:', err));
      
      alert('크루가 성공적으로 생성되었습니다!');
    } catch (error: any) {
      console.error('❌ 크루 생성 실패:', error);
      alert(`크루 생성에 실패했습니다: ${error.message}`);
    } finally {
      setIsCreatingCrew(false);
    }
  };

  // 크루 가입 신청
  const handleJoinCrew = async (crew: Crew) => {
    try {
      await crewAPI.joinCrew(crew.id);
      alert('크루 가입 신청이 완료되었습니다! 크루장의 승인을 기다려주세요.');
      setShowJoinCrewModal(false);
      
      // 캐시 초기화 및 목록 새로고침
      lastFetchedAtRef.current = 0;
      await loadMyCrews();
      await loadAvailableCrews();
    } catch (error: any) {
      console.error('❌ 크루 가입 신청 실패:', error);
      alert(`크루 가입 신청에 실패했습니다: ${error.message}`);
    }
  };

  // 크루 나가기
  const handleLeaveCrew = async (crew: Crew) => {
    if (!confirm(`정말로 "${crew.name}" 크루에서 나가시겠습니까?`)) {
      return;
    }

    try {
      await crewAPI.leaveCrew(crew.id);
      
      // 즉시 UI에서 제거
      setMyCrews(prev => prev.filter(c => c.id !== crew.id));
      
      // 캐시 초기화 및 백그라운드 새로고침
      lastFetchedAtRef.current = 0;
      loadMyCrews().catch(err => console.error('백그라운드 새로고침 실패:', err));
      loadAvailableCrews().catch(err => console.error('백그라운드 새로고침 실패:', err));
      
      alert('크루에서 탈퇴했습니다.');
    } catch (error: any) {
      console.error('❌ 크루 탈퇴 실패:', error);
      alert(`크루 탈퇴에 실패했습니다: ${error.message}`);
    }
  };

  // 크루 삭제
  const handleDeleteCrew = async (crew: Crew) => {
    if (!confirm(`정말로 "${crew.name}" 크루를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    try {
      await crewAPI.deleteCrew(crew.id);
      alert('크루가 삭제되었습니다.');
      setShowCrewDetailModal(false);
      await loadMyCrews();
    } catch (error: any) {
      console.error('❌ 크루 삭제 실패:', error);
      alert(`크루 삭제에 실패했습니다: ${error.message}`);
    }
  };

  // 가입 신청 수 상태 관리
  const [joinRequestCounts, setJoinRequestCounts] = useState<Record<string, number>>({});

  // 가입 신청 목록 로드
  const loadJoinRequests = async (crew: Crew) => {
    try {
      setIsLoading(true);
      const requests = await crewAPI.getJoinRequests(crew.id);
      setJoinRequests(requests);
      setSelectedCrew(crew);
      setShowJoinRequestsModal(true);
    } catch (error) {
      console.error('❌ 가입 신청 목록 로드 실패:', error);
      alert('가입 신청 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 내가 크루장인 크루들의 가입 신청 수 로드
  const loadJoinRequestCounts = useCallback(async () => {
    const leaderCrews = myCrews.filter(crew => isCrewLeader(crew));
    const counts: Record<string, number> = {};
    
    await Promise.all(
      leaderCrews.map(async (crew) => {
        try {
          const requests = await crewAPI.getJoinRequests(crew.id);
          counts[crew.id] = requests.length;
        } catch (error) {
          console.error(`가입 신청 수 조회 실패 (${crew.name}):`, error);
          counts[crew.id] = 0;
        }
      })
    );
    
    setJoinRequestCounts(counts);
  }, [myCrews, user]);

  // 가입 신청 승인/거부
  const handleProcessJoinRequest = async (request: CrewJoinRequest, status: 'APPROVED' | 'REJECTED') => {
    try {
      await crewAPI.processJoinRequest(selectedCrew!.id, request.id, status);
      alert(`가입 신청이 ${status === 'APPROVED' ? '승인' : '거부'}되었습니다.`);
      
      // 신청 목록 새로고침
      await loadJoinRequests(selectedCrew!);
      
      // 배지 업데이트를 위해 신청 수 다시 로드
      await loadJoinRequestCounts();
      
      // 승인 시 크루 멤버 목록도 새로고침
      if (status === 'APPROVED') {
        await loadMyCrews();
      }
    } catch (error: any) {
      console.error('❌ 가입 신청 처리 실패:', error);
      alert(`가입 신청 처리에 실패했습니다: ${error.message}`);
    }
  };

  // 초기 로드
  useEffect(() => {
    loadMyCrews();
    loadAvailableCrews();
  }, [loadMyCrews, loadAvailableCrews]);

  // 크루장인 크루의 가입 신청 수 로드
  useEffect(() => {
    if (myCrews.length > 0) {
      loadJoinRequestCounts();
    }
  }, [myCrews, loadJoinRequestCounts]);

  // 크루 멤버 여부 확인
  const isCrewMember = (crew: Crew) => {
    // selectedCrew.members의 member.id는 사용자 ID로 반환됨
    return Array.isArray(crew.members) && crew.members.some(member => member.id === user?.id);
  };

  // 멤버 상세 토글 상태 (오늘 묵상 공유 상세)
  const [expandedMembers, setExpandedMembers] = useState<Record<string, boolean>>({});
  const [memberShareMap, setMemberShareMap] = useState<Record<string, any>>({});
  const toggleMember = async (memberId: string) => {
    setExpandedMembers(prev => ({ ...prev, [memberId]: !prev[memberId] }));
    const opened = !expandedMembers[memberId];
    // on-demand 로딩: selectedCrew.members의 share가 없으면 API로 조회
    if (opened && selectedCrew) {
      const member = (selectedCrew.members || []).find((m: any) => m.id === memberId);
      if (member && !member.share && !memberShareMap[memberId]) {
        try {
          const detail = await crewAPI.getMemberMeditationToday(memberId);
          setMemberShareMap(prev => ({ ...prev, [memberId]: detail }));
        } catch (e) {
          // 없는 경우는 무시
        }
      }
    }
  };

  // 크루 초대 링크 공유
  const handleShareCrewInvite = async (crew: Crew) => {
    const inviteUrl = `${window.location.origin}/?inviteCrew=${crew.id}`;
    const shareText = `🙏 "${crew.name}" 크루에 초대합니다!\n\n매일 말씀과 함께하는 신앙생활을 나눠요.\n\n`;

    try {
      // Web Share API 지원 확인
      if (navigator.share) {
        await navigator.share({
          title: `${crew.name} 크루 초대`,
          text: shareText,
          url: inviteUrl,
        });
        console.log('✅ 크루 초대 링크 공유 완료');
      } else {
        // Web Share API 미지원 시 클립보드 복사
        await navigator.clipboard.writeText(shareText + inviteUrl);
        alert('초대 링크가 클립보드에 복사되었습니다!\n친구에게 공유해주세요 😊');
        console.log('✅ 크루 초대 링크 복사 완료:', inviteUrl);
      }
    } catch (error) {
      console.error('❌ 크루 초대 링크 공유 실패:', error);
      // 실패 시 수동으로 링크 표시
      if (confirm(`초대 링크:\n${inviteUrl}\n\n확인을 누르면 링크를 복사합니다.`)) {
        try {
          await navigator.clipboard.writeText(inviteUrl);
          alert('링크가 복사되었습니다!');
        } catch (e) {
          alert('수동으로 복사해주세요:\n' + inviteUrl);
        }
      }
    }
  };

  const formatTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    try {
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return d.toISOString().slice(11, 16);
    }
  };

  // UI helpers
  const getInitial = (name?: string) => {
    if (!name) return '크';
    return name.trim().slice(0, 1);
  };

  // 공식 크루 필터링
  const officialCrews = availableCrews.filter(crew => crew.isOfficial === true);

  return (
    <div className="pb-28 sm:pb-32">
      {/* 서브 탭 */}
      <div className="flex mb-4 border-b border-slate-200">
        <button
          onClick={() => setActiveSubTab('my-crews')}
          className={`flex-1 py-3 text-base font-semibold transition-colors relative ${
            activeSubTab === 'my-crews'
              ? 'text-rose-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          내 크루 ({myCrews.length})
          {activeSubTab === 'my-crews' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-rose-600" />
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('official-crews')}
          className={`flex-1 py-3 text-base font-semibold transition-colors relative ${
            activeSubTab === 'official-crews'
              ? 'text-rose-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          e2b 공식크루 ({officialCrews.length})
          {activeSubTab === 'official-crews' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-rose-600" />
          )}
        </button>
        </div>

      {/* 크루 만들기 버튼 */}
      <div className="mb-3">
        {isAuthenticated && !isGuestMode && activeSubTab === 'my-crews' && (
          <button
            onClick={() => setShowCreateCrewModal(true)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-white font-semibold bg-gradient-to-r from-rose-500 to-pink-500 shadow-md active:scale-[.99] transition"
          >
            <Plus className="w-5 h-5" />
            새 크루 만들기
          </button>
        )}
      </div>

      {/* 내 크루 탭 콘텐츠 */}
      {activeSubTab === 'my-crews' && (
        <div>
          {myCrews.length > 0 ? (
          <div className="space-y-3">
            {myCrews.map((crew) => (
                <CrewCard
                key={crew.id}
                  data={{
                    id: crew.id,
                    name: crew.name,
                    memberCount: crew.memberCount,
                    progressPercent: crew.progressPercent ?? 0,
                    isLeader: isCrewLeader(crew),
                    isJoined: true,
                    hasPending: false,
                    joinBadgeCount: joinRequestCounts[crew.id] || 0,
                  }}
                onClick={() => loadCrewDetail(crew.id)}
                  onLeave={() => handleLeaveCrew(crew)}
                  onOpenRequests={() => loadJoinRequests(crew)}
                />
              ))}
                    </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">아직 참여한 크루가 없습니다.</p>
              <p className="text-xs mt-1">새 크루를 만들어보세요!</p>
                      </div>
          )}
        </div>
      )}

      {/* e2b 공식크루 탭 콘텐츠 */}
      {activeSubTab === 'official-crews' && (
      <div>
          {officialCrews.length > 0 ? (
            <div className="space-y-3">
              {officialCrews.map((crew) => {
                const isJoined = myCrews.some(c => c.id === crew.id);
                const myCrewData = myCrews.find(c => c.id === crew.id);
                return (
                  <CrewCard
              key={crew.id}
                    data={{
                      id: crew.id,
                      name: crew.name,
                      memberCount: crew.memberCount,
                      progressPercent: crew.progressPercent ?? 0,
                      isLeader: isJoined && myCrewData ? isCrewLeader(myCrewData) : false,
                      isJoined: isJoined,
                      hasPending: false,
                      joinBadgeCount: isJoined ? (joinRequestCounts[crew.id] || 0) : 0,
                    }}
              onClick={() => loadCrewDetail(crew.id)}
                    onJoin={() => handleJoinCrew(crew)}
                    onLeave={() => handleLeaveCrew(crew)}
                    onOpenRequests={isJoined && myCrewData && isCrewLeader(myCrewData) ? () => loadJoinRequests(crew) : undefined}
                  />
                );
              })}
                </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">공식 크루가 없습니다.</p>
              </div>
          )}
                </div>
              )}

      {/* 크루 생성 모달 */}
      {showCreateCrewModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh'
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">새 크루 만들기</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  크루명 *
                </label>
                <input
                  type="text"
                  value={crewName}
                  onChange={(e) => setCrewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="크루명을 입력하세요"
                  maxLength={30}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  크루 설명
                </label>
                <textarea
                  value={crewDescription}
                  onChange={(e) => setCrewDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="크루에 대한 설명을 입력하세요"
                  rows={3}
                  maxLength={200}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  크루 색상
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={crewColor}
                    onChange={(e) => setCrewColor(e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={crewColor}
                    onChange={(e) => setCrewColor(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#64748b"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  최대 멤버 수
                </label>
                <input
                  type="number"
                  value={crewMaxMembers}
                  onChange={(e) => setCrewMaxMembers(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="2"
                  max="100"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateCrewModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleCreateCrew}
                disabled={isCreatingCrew || !crewName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreatingCrew ? '생성 중...' : '생성하기'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 크루 상세 모달 */}
      {showCrewDetailModal && selectedCrew && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh'
          }}
        >
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* 헤더 - 다크 블루 배경 */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-5 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 mb-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl font-bold"
                    style={{ backgroundColor: selectedCrew.color || '#64748b' }}
                  >
                    {selectedCrew.name?.charAt(0) || '크'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white">{selectedCrew.name}</h3>
                    <p className="text-slate-300 text-sm">{selectedCrew.name}</p>
                  </div>
                </div>
                
                {/* 액션 버튼들 */}
                <div className="flex items-center gap-2">
                  {/* 크루장인 경우 편집 버튼 표시 */}
                  {isCrewLeader(selectedCrew) && onOpenCrewEditModal && (
                    <button
                      onClick={() => {
                        setShowCrewDetailModal(false);
                        onOpenCrewEditModal(selectedCrew);
                      }}
                      className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                      title="크루 정보 수정"
                    >
                      <Edit3 size={20} className="text-white" />
                    </button>
                  )}
                  
                  {/* 닫기 버튼 */}
                  <button
                    onClick={() => setShowCrewDetailModal(false)}
                    className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* 스크롤 가능한 컨텐츠 영역 */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
            {selectedCrew.description && (
                <p className="text-slate-600 mb-4 text-sm">{selectedCrew.description}</p>
            )}

              {/* 통계 섹션 - 3열 레이아웃 */}
            {(() => {
              const completed = selectedCrew.todayCompletedCount || (selectedCrew.members || []).filter((m:any) => m.hasSharedToday).length;
              const percent = selectedCrew.progressPercent !== undefined 
                ? selectedCrew.progressPercent 
                : (selectedCrew.memberCount > 0 ? Math.round((completed / selectedCrew.memberCount) * 100) : 0);
              return (
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    {/* 총 멤버 */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-slate-800">{selectedCrew.memberCount}</div>
                    <div className="text-xs text-slate-500 mt-1">총 멤버</div>
                  </div>
                    {/* 오늘 참여율 - 빨간색 강조 */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-rose-600">{percent}%</div>
                      <div className="text-xs text-slate-500 mt-1">오늘 참여율</div>
                  </div>
                    {/* 최대 인원 */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-slate-800">{selectedCrew.maxMembers || 50}</div>
                    <div className="text-xs text-slate-500 mt-1">최대 인원</div>
                  </div>
                </div>
              );
            })()}
            
              {/* 크루장 섹션 */}
              {(() => {
                const leader = selectedCrew.members.find((m: any) => m.role === 'LEADER' || m.id === selectedCrew.leaderId);
                if (!leader) return null;
                
                const open = !!expandedMembers[leader.id];
                const effectiveShare = leader.share || memberShareMap[leader.id] || null;
                const isCompleted = leader.hasSharedToday || !!leader.completedAt;
                const completedTime = leader.completedAt ? formatTime(leader.completedAt) : null;
                
                return (
            <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-slate-600" />
                      <h4 className="font-bold text-slate-800">크루장</h4>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                      <button type="button" onClick={() => toggleMember(leader.id)} className="w-full p-4">
                        <div className="flex items-center gap-3">
                          {/* 아바타 */}
                          <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center text-white font-bold text-lg shrink-0">
                            {leader.name?.charAt(0) || 'L'}
                          </div>
                          {/* 정보 */}
                          <div className="flex-1 min-w-0 text-left">
                            <div className="font-bold text-slate-900 text-base">{leader.name}</div>
                            <div className="text-sm text-slate-500">
                              {isCompleted ? '오늘 묵상 완료' : '아직 묵상하지 않음'}
                            </div>
                          </div>
                          {/* 상태 표시 */}
                          <div className="flex items-center gap-2 shrink-0">
                            {isCompleted ? (
                              <>
                                <span className="text-sm font-medium text-green-600">{completedTime || '완료'}</span>
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              </>
                            ) : (
                              <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
                            )}
                          </div>
                        </div>
                      </button>
                      {open && (
                        leader.hasSharedToday && effectiveShare ? (
                          <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-4">
                          {/* 말씀 섹션 - 피드 상세 디자인과 동일 */}
                          {(effectiveShare.verse || effectiveShare.verseText || effectiveShare.verseReference) && (
                            <div className="bg-gradient-to-r from-rose-50 to-orange-50 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border-l-4 border-rose-300">
                              <div className="flex items-start gap-2 mb-3">
                                <BookOpen size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
                                <h4 className="text-base sm:text-lg font-bold text-rose-700 leading-tight">{effectiveShare.verseReference || '오늘의 말씀'}</h4>
                              </div>
                              <p className="text-slate-700 text-sm sm:text-base font-medium italic leading-relaxed">{effectiveShare.verse || effectiveShare.verseText}</p>
                            </div>
                          )}

                          {/* 단계 1 - 하나님의 뜻 */}
                          {effectiveShare.godsWill && (
                            <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                              <div className="flex items-start gap-3 mb-3 sm:mb-4">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-blue-600 font-bold text-xs sm:text-sm">1</span>
                                </div>
                                <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">하나님이 나에게 원하시는 것은?</h4>
                              </div>
                              <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{effectiveShare.godsWill}</p>
                            </div>
                          )}

                          {/* 단계 2 - 변화/성장 */}
                          {effectiveShare.growthChange && (
                            <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                              <div className="flex items-start gap-3 mb-3 sm:mb-4">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-green-600 font-bold text-xs sm:text-sm">2</span>
                                </div>
                                <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">내가 변화성장해야 하는 것은?</h4>
                              </div>
                              <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{effectiveShare.growthChange}</p>
                            </div>
                          )}

                          {/* 단계 3 - 기도 (공개/요청) */}
                          {effectiveShare.prayer && effectiveShare.prayerShareType !== 'private' && (
                            <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                              <div className="flex items-start gap-3 mb-3 sm:mb-4">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-purple-600 font-bold text-xs sm:text-sm">3</span>
                                </div>
                                <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">{effectiveShare.prayerShareType === 'request' ? '중보기도 요청 🙏' : '이 말씀으로 기도해보자'}</h4>
                              </div>
                              <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{effectiveShare.prayer}</p>
                              {effectiveShare.prayerShareType === 'request' && (
                                <div className="mt-3 sm:mt-4 p-3 bg-purple-50 rounded-xl sm:rounded-2xl ml-9 sm:ml-11">
                                  <p className="text-xs sm:text-sm text-purple-700 font-medium">💜 함께 중보기도 해주세요</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        ) : (
                          <div className="px-4 pb-4 border-t border-slate-100 pt-4">
                            <div className="text-center py-8 text-slate-400">
                              <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">아직 나눔이 없습니다</p>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* 멤버 목록 섹션 */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-slate-600" />
                  <h4 className="font-bold text-slate-800">멤버 목록</h4>
                </div>
              <div className="space-y-2">
                  {selectedCrew.members.filter((m: any) => m.role !== 'LEADER' && m.id !== selectedCrew.leaderId).map((member: any) => {
                  const open = !!expandedMembers[member.id];
                  const effectiveShare = member.share || memberShareMap[member.id] || null;
                    const isCompleted = member.hasSharedToday || !!member.completedAt;
                    const completedTime = member.completedAt ? formatTime(member.completedAt) : null;
                    
                  return (
                      <div key={member.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <button type="button" onClick={() => toggleMember(member.id)} className="w-full p-4">
                          <div className="flex items-center gap-3">
                            {/* 아바타 */}
                            <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center text-white font-bold text-lg shrink-0">
                              {member.name?.charAt(0) || 'M'}
                            </div>
                            {/* 정보 */}
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <div className="font-bold text-slate-900 text-base">{member.name}</div>
                                {/* 100번 달성 메달 (100회 이상만 표시) */}
                                {member.totalMeditationCount && member.totalMeditationCount >= 100 && (
                                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full text-[10px] font-bold shadow-sm">
                                    <Trophy size={10} />
                                    <span>100회</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-sm text-slate-500">
                                  {isCompleted ? '오늘 묵상 완료' : '아직 묵상하지 않음'}
                                </div>
                                {/* 총 묵상 횟수 (기본으로 표시) */}
                                {member.totalMeditationCount !== undefined && (
                                  <span className="text-xs text-slate-600 font-medium">
                                    • {member.totalMeditationCount}회
                                  </span>
                                )}
                              </div>
                              {/* 기도완료 메달 */}
                              {(() => {
                                const achievement = getMemberAchievement(member.id);
                                if (achievement) {
                                  return (
                                    <div className="mt-2">
                                      <PrayerMedal
                                        isGoalMet={achievement.isDailyGoalMet}
                                        actualDuration={achievement.actualDuration}
                                        dailyGoal={achievement.dailyGoal}
                                        size="sm"
                                      />
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            {/* 상태 표시 */}
                            <div className="flex items-center gap-2 shrink-0">
                              {isCompleted ? (
                                <>
                                  <span className="text-sm font-medium text-green-600">{completedTime || '완료'}</span>
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                </>
                              ) : (
                                <div className="w-2 h-2 bg-slate-300 rounded-full"></div>
                              )}
                          </div>
                        </div>
                      </button>
                      {open && (
                        member.hasSharedToday && effectiveShare ? (
                            <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-4">
                            {/* 말씀 섹션 - 피드 상세 디자인과 동일 */}
                            {(effectiveShare.verse || effectiveShare.verseText || effectiveShare.verseReference) && (
                              <div className="bg-gradient-to-r from-rose-50 to-orange-50 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border-l-4 border-rose-300">
                                <div className="flex items-start gap-2 mb-3">
                                  <BookOpen size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
                                  <h4 className="text-base sm:text-lg font-bold text-rose-700 leading-tight">{effectiveShare.verseReference || '오늘의 말씀'}</h4>
                                </div>
                                <p className="text-slate-700 text-sm sm:text-base font-medium italic leading-relaxed">{effectiveShare.verse || effectiveShare.verseText}</p>
                              </div>
                            )}

                            {/* 단계 1 - 하나님의 뜻 */}
                            {effectiveShare.godsWill && (
                              <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                                <div className="flex items-start gap-3 mb-3 sm:mb-4">
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-blue-600 font-bold text-xs sm:text-sm">1</span>
                                  </div>
                                  <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">하나님이 나에게 원하시는 것은?</h4>
                                </div>
                                <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{effectiveShare.godsWill}</p>
                              </div>
                            )}

                            {/* 단계 2 - 변화/성장 */}
                            {effectiveShare.growthChange && (
                              <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                                <div className="flex items-start gap-3 mb-3 sm:mb-4">
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-green-600 font-bold text-xs sm:text-sm">2</span>
                                  </div>
                                  <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">내가 변화성장해야 하는 것은?</h4>
                                </div>
                                <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{effectiveShare.growthChange}</p>
                              </div>
                            )}

                            {/* 단계 3 - 기도 (공개/요청) */}
                            {effectiveShare.prayer && effectiveShare.prayerShareType !== 'private' && (
                              <div className="bg-white border-2 border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                                <div className="flex items-start gap-3 mb-3 sm:mb-4">
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-purple-600 font-bold text-xs sm:text-sm">3</span>
                                  </div>
                                  <h4 className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">{effectiveShare.prayerShareType === 'request' ? '중보기도 요청 🙏' : '이 말씀으로 기도해보자'}</h4>
                                </div>
                                <p className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap pl-9 sm:pl-11">{effectiveShare.prayer}</p>
                                {effectiveShare.prayerShareType === 'request' && (
                                  <div className="mt-3 sm:mt-4 p-3 bg-purple-50 rounded-xl sm:rounded-2xl ml-9 sm:ml-11">
                                    <p className="text-xs sm:text-sm text-purple-700 font-medium">💜 함께 중보기도 해주세요</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {member.completedAt && (
                              <div className="text-xs text-gray-400">완료: {formatTime(member.completedAt)}</div>
                            )}
                          </div>
                        ) : (
                            <div className="px-4 pb-4 text-sm text-slate-500 bg-slate-50 border-t border-slate-100 pt-4">
                            오늘 공유된 묵상 내용이 없습니다.
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
              {/* 액션 버튼들 - 하단 고정 */}
              {isAuthenticated && !isGuestMode && (
                <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
                  <div className="flex gap-2">
                  {isCrewLeader(selectedCrew) && (
                    <>
                      <button
                        onClick={() => loadJoinRequests(selectedCrew)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-semibold"
                      >
                        <UserCheck className="w-4 h-4" />
                          가입 신청
                      </button>
                      <button
                        onClick={() => handleShareCrewInvite(selectedCrew)}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold"
                        title="친구 초대"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCrew(selectedCrew)}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  
                  {isCrewMember(selectedCrew) && !isCrewLeader(selectedCrew) && (
                    <>
                      <button
                        onClick={() => handleShareCrewInvite(selectedCrew)}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold"
                        title="친구 초대"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    <button
                      onClick={() => handleLeaveCrew(selectedCrew)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
                    >
                      <UserX className="w-4 h-4" />
                      크루 나가기
                    </button>
                    </>
                  )}
                  
                  {!isCrewMember(selectedCrew) && !isCrewLeader(selectedCrew) && (
                    <button
                      onClick={() => handleJoinCrew(selectedCrew)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors font-semibold"
                    >
                      <UserPlus className="w-4 h-4" />
                      크루 가입하기
                    </button>
                  )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 가입 신청 관리 모달 */}
      {showJoinRequestsModal && selectedCrew && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh'
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{selectedCrew.name} - 가입 신청</h3>
              <button
                onClick={() => setShowJoinRequestsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {joinRequests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">대기 중인 가입 신청이 없습니다.</p>
            ) : (
              <div className="space-y-3">
                {joinRequests.map((request) => (
                  <div key={request.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <div className="font-medium">{request.user.name}</div>
                        {request.message && (
                          <div className="text-sm text-gray-500">{request.message}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleProcessJoinRequest(request, 'APPROVED')}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                      >
                        승인
                      </button>
                      <button
                        onClick={() => handleProcessJoinRequest(request, 'REJECTED')}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                      >
                        거부
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* 가입 모달: 검색/필터 진입용 */}
      {showJoinCrewModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">크루 찾기</h3>
              <button onClick={() => setShowJoinCrewModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="mb-4">
              <input
                type="text"
                placeholder="크루명으로 검색..."
                onChange={(e) => {
                  const q = e.target.value.toLowerCase();
                  setAvailableCrews(prev => prev.filter(c => c.name.toLowerCase().includes(q)));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availableCrews.map(crew => (
                <div key={crew.id} className="border rounded-lg p-3 hover:shadow cursor-pointer" onClick={() => {
                  setShowJoinCrewModal(false);
                  loadCrewDetail(crew.id);
                }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: crew.color }} />
                    <div className="font-semibold text-gray-800">{crew.name}</div>
                  </div>
                  <div className="text-xs text-gray-500">멤버 {crew.memberCount}명 • {crew.leader.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CrewSystem;
